import baseScmService from 'services/baseScmService';

const coursePauseRequestService = baseScmService.injectEndpoints({
  endpoints: (build) => ({
    getSbatLectures: build.query({
      query: ({ superBatch, fromDate, toDate }) => ({
        url: '/course-pause-requests/super-batch-academy-topics',
        params: {
          super_batch_id: superBatch.id,
          'filter[from]': fromDate,
          'filter[to]': toDate,
        },
      }),
      providesTags: (result, error, { superBatch }) => {
        return error
          ? []
          : [{ type: 'SuperBatchAcademyTopic', id: superBatch.id }];
      },
    }),
    getMenteeSbatLectures: build.query({
      query: ({ request, fromDate, toDate }) => ({
        url: '/course-pause-requests/mentee-academy-topics',
        params: {
          id: request.id,
          'filter[from]': fromDate,
          'filter[to]': toDate,
        },
      }),
      providesTags: (result, error, { request }) => {
        return error
          ? []
          : [{ type: 'SuperBatchAcademyTopic', id: request.id }];
      },
    }),
    createNote: build.mutation({
      query: ({ requestId, note }) => ({
        method: 'POST',
        url: `/course-pause-requests/${requestId}/notes`,
        body: {
          note: {
            content: note,
          },
        },
      }),
      invalidatesTags: (result, error, { requestId }) =>
        error ? [] : [{ type: 'CoursePauseRequest', id: requestId }],
    }),
    rescheduleRequest: build.mutation({
      query: ({ requestId, toSbatId }) => ({
        method: 'PUT',
        url: `/course-pause-requests/${requestId}/reschedule`,
        body: {
          to_sbat_id: toSbatId,
        },
      }),
      invalidatesTags: (result, error, { requestId }) =>
        error ? [] : [{ type: 'CoursePauseRequest', id: requestId }],
    }),
    scheduleResume: build.mutation({
      query: ({ requestId, toSbatId }) => ({
        method: 'POST',
        url: `/course-pause-requests/${requestId}/schedule-resume`,
        body: {
          to_sbat_id: toSbatId,
        },
      }),
      invalidatesTags: (result, error) =>
        error ? [] : [{ type: 'CoursePauseRequest' }],
    }),
    cancelRequest: build.mutation({
      query: ({ requestId }) => ({
        method: 'PUT',
        url: `/course-pause-requests/${requestId}/cancel`,
      }),
      invalidatesTags: (result, error, { requestId }) =>
        error ? [] : [{ type: 'CoursePauseRequest', id: requestId }],
    }),
    updateRequestedRequest: build.mutation({
      query: ({ requestId, duration, streakEnabled }) => ({
        method: 'PUT',
        url: `/course-pause-requests/${requestId}/mark-as-pending`,
        body: {
          duration_of_pause: duration,
          enable_streak: streakEnabled,
        },
      }),
      invalidatesTags: (result, error, { requestId }) =>
        error ? [] : [{ type: 'CoursePauseRequest', id: requestId }],
    }),
    approveRequest: build.mutation({
      query: ({ requestId }) => ({
        method: 'PUT',
        url: `/course-pause-requests/${requestId}/approve`,
      }),
      invalidatesTags: (result, error, { requestId }) =>
        error ? [] : [{ type: 'CoursePauseRequest', id: requestId }],
    }),
    unscheduleRequest: build.mutation({
      query: ({ requestId }) => ({
        method: 'PUT',
        url: `/course-pause-requests/${requestId}/unschedule`,
      }),
      invalidatesTags: (result, error) =>
        error ? [] : [{ type: 'CoursePauseRequest' }],
    }),
    updateDuration: build.mutation({
      query: ({
        requestId,
        durationOfPause,
        extensionDuration,
        extensionSource,
      }) => ({
        method: 'PUT',
        url: `/course-pause-requests/${requestId}`,
        body: {
          duration_of_pause: durationOfPause,
          extension_duration: extensionDuration,
          extension_source: extensionSource,
        },
      }),
      invalidatesTags: (result, error, { requestId }) =>
        error ? [] : [{ type: 'CoursePauseRequest', id: requestId }],
    }),
    getCoursePauseRequests: build.query({
      query: ({
        filters,
        sortOrder,
        coursePauseType,
        coursePauseStatus,
        page = 1,
        pageLimit = 50,
      }) => {
        const {
          learnerEmail,
          currentBatch,
          newBatch,
          pauseDuration,
          resumeDate,
          fromToBeResumedDate,
          untilToBeResumedDate,
          courseType,
        } = filters;
        const { field, type } = sortOrder;

        return {
          url: '/course-pause-requests',
          params: {
            'filters[course_pause_request][status]': coursePauseStatus,
            'filters[course_pause_request][type]': coursePauseType,
            'filters[course_pause_request][duration_of_pause]': pauseDuration,
            'filters[course_pause_request][resume_date]': resumeDate,
            'filters[course_pause_request][expected_resume_date][greater_than]':
              fromToBeResumedDate,
            'filters[course_pause_request][expected_resume_date][less_than]':
              untilToBeResumedDate,
            'filters[user][course_type]': courseType,
            'filters[user][email]': learnerEmail,
            'filters[user][current_super_batch]': currentBatch,
            'filters[user][new_super_batch]': newBatch,
            [`sort_order[${field}]`]: type,
            page,
            page_limit: pageLimit,
          },
        };
      },
      providesTags: (result, error) => {
        if (error) return [];
        if (result?.data?.length > 0) {
          const requests = result.data;

          return requests.map((req) => ({
            type: 'CoursePauseRequest',
            id: req.id,
          }));
        }
        return [];
      },
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetSbatLecturesQuery,
  useGetMenteeSbatLecturesQuery,
  useScheduleResumeMutation,
  useCancelRequestMutation,
  useUpdateRequestedRequestMutation,
  useUpdateDurationMutation,
  useCreateNoteMutation,
  useGetCoursePauseRequestsQuery,
  useRescheduleRequestMutation,
  useUnscheduleRequestMutation,
  useApproveRequestMutation,
} = coursePauseRequestService;

export default coursePauseRequestService;
